import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import RemoveDestinationFromConnectionModal from "../destinations/RemoveDestinationFromConnectionModal";

type DestinationActionsMenuProps = {
    onOpenRules: () => void;
    onDelete: () => void;
    destinationName?: string;
};

const DestinationActionsMenu = ({ onOpenRules, onDelete, destinationName }: DestinationActionsMenuProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [open]);

    const handleDeleteClick = () => {
        // Close the dropdown first, then surface the confirmation modal. The destination
        // isn't actually deleted — it's just detached from this connection.
        setOpen(false);
        setShowConfirm(true);
    };

    const handleConfirmDelete = () => {
        setShowConfirm(false);
        onDelete();
    };

    const handleSelect = (action: () => void) => {
        setOpen(false);
        action();
    };

    const label = destinationName ? `${t("Actions")} ${destinationName}` : t("Actions");

    return (
        <>
            <div ref={containerRef} className="relative inline-block">
                <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label={label}
                    onClick={() => setOpen((prev) => !prev)}
                    className="text-secondary-gray hover:text-off-black px-2 py-1 transition-colors duration-200 text-xl leading-none cursor-pointer"
                    data-testid="DestinationActionsMenu-trigger"
                >
                    ⋮
                </button>
                {open && (
                    <div
                        role="menu"
                        className="absolute right-0 mt-1 w-44 bg-white border border-gray-lines rounded shadow-lg z-30"
                        data-testid="DestinationActionsMenu-menu"
                    >
                        <button
                            type="button"
                            role="menuitem"
                            onClick={() => handleSelect(onOpenRules)}
                            className="block w-full text-left px-4 py-2 text-sm text-off-black hover:bg-light-gray transition-colors"
                            data-testid="DestinationActionsMenu-rules"
                        >
                            {t("Rules")}
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            onClick={handleDeleteClick}
                            className="block w-full text-left px-4 py-2 text-sm text-orange hover:bg-very-light-orange transition-colors"
                            data-testid="DestinationActionsMenu-delete"
                        >
                            {t("Remove destination")}
                        </button>
                    </div>
                )}
            </div>
            {showConfirm && (
                <RemoveDestinationFromConnectionModal
                    destinationName={destinationName ?? ""}
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setShowConfirm(false)}
                />
            )}
        </>
    );
};

export default DestinationActionsMenu;
